import {toast} from '@wandb/weave/common/components/elements/Toast';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React, {useCallback, useState} from 'react';

import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {useIsMobile} from '../../../util/panelbankFlow';
import {type ImageExportType} from '../../../util/panelExport';
import {type getPanelSpec} from '../../../util/panelTypes';
import {PanelViewSourceDropdownItem} from '../../PanelMarimo/PanelViewSourceDropdownItem';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {PanelDeleteMenuItem} from '../common/PanelDeleteMenuItem';
import {PanelOverflowMenu} from '../common/PanelOverflowMenu';
import {useGoToBaseUrl} from '../hooks/hooks';
import {usePanelContext} from '../PanelContextProvider';
import {PanelExportMenu} from '../PanelExportModal/PanelExportMenu';
import {PanelExportModalContainer} from '../PanelExportModal/PanelExportModalContainer';
import {type ExportType} from '../types';
import {FullScreenPanelCopyLinkMenuItem} from './FullScreenPanelCopyLinkMenuItem';

type FullScreenPanelOverflowMenuProps = {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
};

export const FullScreenPanelOverflowMenu = ({
  panelBankSectionConfigRef,
  panelSpec,
}: FullScreenPanelOverflowMenuProps) => {
  const isMobileLayout = useIsMobile();
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();
  const {panel, panelRef, trackPanelControls} = usePanelContext();
  const goToBaseURL = useGoToBaseUrl();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const deletePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );
  const onRemovePanel = useCallback(() => {
    deletePanel(panelRef, panelBankConfigRef, workspaceSettingsRef);
    goToBaseURL();
    toast('Panel deleted');
    trackPanelControls({action: 'Panel deleted'});
  }, [
    deletePanel,
    panelRef,
    panelBankConfigRef,
    workspaceSettingsRef,
    goToBaseURL,
    trackPanelControls,
  ]);

  if (isMobileLayout) {
    return null;
  }

  return (
    <>
      <PanelExportModalContainer
        exportType={exportType}
        imageExportType={imageExportType}
        panelType={panelSpec.type}
        unsetExportType={unsetExportType}
      />
      <PanelOverflowMenu
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        buttonSize="md">
        <DropdownMenu.Content align="start" className="mr-12">
          {panel.viewType === 'Marimo' && (
            <PanelViewSourceDropdownItem panelRef={panelRef} />
          )}
          <FullScreenPanelCopyLinkMenuItem />
          <PanelExportMenu
            panelSpec={panelSpec}
            setExportType={setExportType}
            setImageExportType={setImageExportType}
          />
          <DropdownMenu.Separator />
          <PanelDeleteMenuItem onSelect={onRemovePanel} />
        </DropdownMenu.Content>
      </PanelOverflowMenu>
    </>
  );
};
